import { useState } from "react";
import { toast } from "sonner";
import { X, Loader2, FileText, Heart, Camera, MessageCircle, Palette, Copy, Check, Send, Search, Download } from "lucide-react";
import { Button } from "./ui/button";
import api from "../lib/api";

const ACTIONS = [
  { id: "post", label: "Write a Post", icon: FileText, placeholder: "What would you like to post about?", color: "text-cyan-400 bg-cyan-500/10" },
  { id: "ecard", label: "Create an Ecard", icon: Heart, placeholder: "Who is this for? What's the occasion?", color: "text-pink-400 bg-pink-500/10" },
  { id: "caption", label: "Caption a Photo", icon: Camera, placeholder: "Describe the photo you want to caption", color: "text-amber-400 bg-amber-500/10" },
  { id: "comment_reply", label: "Draft a Reply", icon: MessageCircle, placeholder: "Paste the post you want to reply to", color: "text-emerald-400 bg-emerald-500/10" },
  { id: "image", label: "Create Art", icon: Palette, placeholder: "Describe the image you want to create", color: "text-violet-400 bg-violet-500/10" },
];

const IMAGE_STYLES = [
  { id: "realistic", label: "Realistic", color: "bg-blue-500/10 text-blue-400 border-blue-500/30" },
  { id: "artistic", label: "Artistic", color: "bg-purple-500/10 text-purple-400 border-purple-500/30" },
  { id: "cartoon", label: "Cartoon", color: "bg-amber-500/10 text-amber-400 border-amber-500/30" },
  { id: "ecard", label: "Ecard", color: "bg-pink-500/10 text-pink-400 border-pink-500/30" },
];

export default function AIStudioModal({ open, onClose, onPublish }) {
  const [step, setStep] = useState("pick"); // pick | prompt | results
  const [action, setAction] = useState(null);
  const [prompt, setPrompt] = useState("");
  const [generating, setGenerating] = useState(false);
  const [results, setResults] = useState([]);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [editText, setEditText] = useState("");
  const [copied, setCopied] = useState(false);
  const [imageStyle, setImageStyle] = useState("realistic");
  const [researchFirst, setResearchFirst] = useState(false);

  if (!open) return null;

  const handleGenerate = async () => {
    if (!prompt.trim()) return;
    setGenerating(true);
    try {
      if (action.id === "image") {
        const r = await api.post("/ai-studio/generate-image", { prompt: prompt.trim(), style: imageStyle });
        setResults([r.data.image_url]);
        setStep("results");
      } else {
        let finalPrompt = prompt.trim();
        if (researchFirst) {
          try {
            const sr = await api.post("/search", { query: finalPrompt, type: "web" });
            if (sr.data.ai_summary) finalPrompt = `Based on this research: "${sr.data.ai_summary.slice(0, 500)}"\n\nNow generate content about: ${finalPrompt}`;
          } catch {}
        }
        const r = await api.post("/ai-studio/generate", { type: action.id, prompt: finalPrompt });
        setResults(r.data.alternatives || [r.data.generated_text]);
        setEditText(r.data.alternatives?.[0] || r.data.generated_text);
        setSelectedIdx(0);
        setStep("results");
      }
    } catch { toast.error("Generation failed. Try again."); }
    finally { setGenerating(false); }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(editText);
    setCopied(true);
    toast.success("Copied!");
    setTimeout(() => setCopied(false), 2000);
  };

  const handlePublish = () => {
    if (onPublish) onPublish(editText);
    handleClose();
  };

  const handleClose = () => {
    setStep("pick");
    setAction(null);
    setPrompt("");
    setResults([]);
    setEditText("");
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={handleClose} data-testid="ai-studio-modal">
      <div className="bg-card rounded-2xl shadow-2xl max-w-xl w-full max-h-[85vh] overflow-y-auto animate-fade-in-up" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border/60">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-cyan-400 to-violet-500 flex items-center justify-center">
              <Palette className="w-3.5 h-3.5 text-white" />
            </div>
            <h2 className="font-outfit text-base font-semibold text-foreground">AI Content Studio</h2>
          </div>
          <button onClick={handleClose} className="text-muted-foreground/70 hover:text-muted-foreground bg-transparent border-0 cursor-pointer"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5">
          {/* Step 1: Pick Action */}
          {step === "pick" && (
            <div className="space-y-2" data-testid="studio-actions">
              <p className="text-sm text-muted-foreground mb-3">What would you like to create?</p>
              {ACTIONS.map((a) => (
                <button
                  key={a.id}
                  onClick={() => { setAction(a); setStep("prompt"); }}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border border-border/60 bg-card hover:bg-white/5 text-left transition-all cursor-pointer hover:-translate-y-0.5`}
                  data-testid={`studio-action-${a.id}`}
                >
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${a.color}`}><a.icon className="w-4 h-4" /></div>
                  <span className="text-sm font-medium text-foreground">{a.label}</span>
                </button>
              ))}
            </div>
          )}

          {/* Step 2: Prompt */}
          {step === "prompt" && action && (
            <div data-testid="studio-prompt">
              <button onClick={() => setStep("pick")} className="text-xs text-muted-foreground hover:text-cyan-400 mb-3 bg-transparent border-0 cursor-pointer">&larr; Back</button>
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${action.color} mb-3`}><action.icon className="w-5 h-5" /></div>
              <h3 className="font-outfit text-base font-medium text-foreground mb-1">{action.label}</h3>

              {/* Image style selector */}
              {action.id === "image" && (
                <div className="flex gap-2 mb-3" data-testid="image-styles">
                  {IMAGE_STYLES.map((s) => (
                    <button key={s.id} onClick={() => setImageStyle(s.id)} className={`px-3 py-1.5 rounded-lg text-xs font-medium border cursor-pointer transition-all ${imageStyle === s.id ? s.color + " ring-1 ring-current" : "border-border bg-card text-muted-foreground"}`} data-testid={`style-${s.id}`}>
                      {s.label}
                    </button>
                  ))}
                </div>
              )}

              <textarea
                data-testid="studio-prompt-input"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder={action.placeholder}
                rows={3}
                className="w-full bg-muted/40 border border-border rounded-lg px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:ring-2 focus:ring-cyan-400/25 resize-none mb-3"
                autoFocus
              />

              {/* Research First toggle */}
              {action.id !== "image" && (
                <label className="flex items-center gap-2 mb-4 cursor-pointer" data-testid="research-toggle">
                  <div className={`w-8 h-4 rounded-full transition-colors relative ${researchFirst ? "bg-indigo-500" : "bg-muted"}`} onClick={() => setResearchFirst(!researchFirst)}>
                    <div className={`w-3.5 h-3.5 rounded-full bg-card shadow-sm absolute top-0.5 transition-transform ${researchFirst ? "translate-x-4" : "translate-x-0.5"}`} />
                  </div>
                  <span className="text-xs text-muted-foreground flex items-center gap-1"><Search className="w-3 h-3" /> Research First</span>
                  <span className="text-[10px] text-muted-foreground/70">AI researches before generating</span>
                </label>
              )}

              <Button
                data-testid="studio-generate-btn"
                onClick={handleGenerate}
                disabled={!prompt.trim() || generating}
                className="w-full bg-gradient-to-r from-cyan-400 to-violet-500 text-white h-10 rounded-lg disabled:opacity-40 press-down"
              >
                {generating ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> {researchFirst ? "Researching & Generating..." : "Generating..."}</> : "Generate"}
              </Button>
            </div>
          )}

          {/* Step 3: Results */}
          {step === "results" && (
            <div data-testid="studio-results">
              <button onClick={() => setStep("prompt")} className="text-xs text-muted-foreground hover:text-cyan-400 mb-3 bg-transparent border-0 cursor-pointer">&larr; Edit prompt</button>

              {action?.id === "image" ? (
                <div className="mb-4">
                  <img src={results[0]} alt="Generated" className="w-full rounded-lg border border-border mb-2" />
                  <p className="text-[10px] text-muted-foreground/70 text-center">Powered by Nano Banana</p>
                </div>
              ) : (
                <>
                  <p className="text-xs text-muted-foreground mb-3">Pick your favorite version:</p>
                  <div className="space-y-2 mb-4">
                    {results.map((r, i) => (
                      <button
                        key={i}
                        onClick={() => { setSelectedIdx(i); setEditText(r); }}
                        className={`w-full text-left p-3 rounded-xl border text-sm transition-all cursor-pointer ${
                          selectedIdx === i ? "border-cyan-400 bg-cyan-500/10 ring-1 ring-cyan-400/30" : "border-border/60 bg-card hover:bg-white/5"
                        }`}
                        data-testid={`studio-option-${i}`}
                      >
                        {r}
                      </button>
                    ))}
                  </div>
                  <textarea
                    data-testid="studio-edit-textarea"
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    rows={3}
                    className="w-full bg-muted/40 border border-border rounded-lg px-4 py-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-cyan-400/25 resize-none mb-4"
                  />
                </>
              )}

              <div className="flex gap-2">
                <button onClick={handleGenerate} className="px-4 py-2 text-xs text-cyan-400 font-medium bg-cyan-500/10 rounded-lg border-0 cursor-pointer hover:bg-cyan-500/15 transition-colors" data-testid="studio-regenerate">Regenerate</button>
                <button onClick={handleCopy} className="px-4 py-2 text-xs text-muted-foreground font-medium bg-muted/70 rounded-lg border-0 cursor-pointer hover:bg-white/10 transition-colors flex items-center gap-1" data-testid="studio-copy">
                  {copied ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
                </button>
                <Button onClick={handlePublish} disabled={!editText.trim()} className="flex-1 bg-gradient-to-r from-cyan-400 to-violet-500 text-white text-xs h-9 rounded-lg disabled:opacity-40 press-down" data-testid="studio-publish">
                  <Send className="w-3 h-3 mr-1" /> Post to Feed
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
